'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ChevronLeft, SaveAltSharp } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

import {
  ReportCampaignsList,
  useBreadcrumbs,
  useDownloadDetailedReport,
  useDownloadSimplifiedReport,
  useGetAdvertiser,
  useGetReportsCampaigns,
} from '~/entities';
import {
  ContentTitle,
  downloadFileFromBlob,
  Pagination,
  StyledContainer,
  StyledTableContainer,
} from '~/shared';
import { CampaignReport, ReportsCampaignsDto } from '~/types';

export type RelatoriosCampanhasProps = {
  advertiserId: string;
};

const REPORTS_CAMPAIGNS_PAGE_SIZE = 15;

export function RelatoriosCampanhasView({
  advertiserId,
}: RelatoriosCampanhasProps) {
  const breadcrumbs = useBreadcrumbs();
  const [reportsFilter, setReportsFilter] = useState<ReportsCampaignsDto>({
    idAdvertiser: advertiserId,
    count: REPORTS_CAMPAIGNS_PAGE_SIZE,
    page: 1,
  });
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);

  const getAdvertiserQuery = useGetAdvertiser(advertiserId);

  const getReportsCampaignsQuery = useGetReportsCampaigns(reportsFilter);

  const { isLoading, isFetching, data } = getReportsCampaignsQuery;

  const downloadSimplifiedMutation = useDownloadSimplifiedReport(
    response => {
      const advertiserName = getAdvertiserQuery.data?.name ?? 'Sem Nome';
      downloadFileFromBlob(
        response,
        `Relatório Simplificado - ${advertiserName}`
      );
    },
    error => {
      console.error('Erro ao baixar relatório simplificado:', error);
    }
  );

  const downloadDetailedMutation = useDownloadDetailedReport(
    response => {
      const advertiserName = getAdvertiserQuery.data?.name ?? 'Sem Nome';
      downloadFileFromBlob(response, `Relatório Detalhado - ${advertiserName}`);
    },
    error => {
      console.error('Erro ao baixar relatório detalhado:', error);
    }
  );

  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / REPORTS_CAMPAIGNS_PAGE_SIZE);
  const contentTitle = getAdvertiserQuery.data?.name ?? 'Carregando';

  useEffect(() => {
    breadcrumbs.setPaths([
      { label: 'Relatórios', path: '/relatorios' },
      { label: contentTitle, path: `/relatorios/${advertiserId}` },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentTitle]);

  useEffect(() => {
    getReportsCampaignsQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectCampaign = (campaignId: string, selected: boolean) => {
    setSelectedCampaigns((prev: string[]) => {
      if (selected) {
        return [...prev, campaignId];
      }
      return prev.filter((id: string) => id !== campaignId);
    });
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      const allCampaignIds =
        data?.list?.map((campaign: CampaignReport) => campaign.idCampaign) ||
        [];
      setSelectedCampaigns(allCampaignIds);
    } else {
      setSelectedCampaigns([]);
    }
  };

  const handleDownloadSimple = () => {
    const campaignIds = selectedCampaigns.length > 0 ? selectedCampaigns : [];

    downloadSimplifiedMutation.mutate({
      IdAdvertiser: advertiserId,
      CampaignIds: campaignIds,
      page: reportsFilter.page,
    });
  };

  const handleDownloadDetailed = () => {
    const campaignIds = selectedCampaigns.length > 0 ? selectedCampaigns : [];

    downloadDetailedMutation.mutate({
      IdAdvertiser: advertiserId,
      CampaignIds: campaignIds,
      page: reportsFilter.page,
    });
  };

  function nextPageHandler() {
    if (reportsFilter.page < totalPages) {
      setReportsFilter((state: ReportsCampaignsDto) => ({
        ...state,
        page: state.page + 1,
      }));
    }
  }

  function prevPageHandler() {
    if (reportsFilter.page > 1) {
      setReportsFilter((state: ReportsCampaignsDto) => ({
        ...state,
        page: state.page - 1,
      }));
    }
  }

  // function handleExportExcel() {
  //   downloadReportsCampaignsMutation.mutate(reportsFilter);
  // }

  const hasSelectedCampaigns = selectedCampaigns.length > 0;
  const isDownloading =
    downloadSimplifiedMutation.isPending || downloadDetailedMutation.isPending;

  return (
    <>
      <ContentTitle title="Relatórios" isFetching={isLoading || isFetching} />

      <StyledContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Link href="/relatorios" passHref>
            <Button variant="text" color="primary">
              <ChevronLeft />
              Voltar
            </Button>
          </Link>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              sx={{ textTransform: 'none' }}
              startIcon={
                downloadSimplifiedMutation.isPending ? (
                  <CircularProgress size={16} />
                ) : (
                  <SaveAltSharp />
                )
              }
              disabled={isDownloading || isLoading}
              onClick={handleDownloadSimple}
            >
              Download Simplificado
            </Button>

            <Button
              variant="contained"
              color="primary"
              size="medium"
              sx={{ textTransform: 'none' }}
              startIcon={
                downloadDetailedMutation.isPending ? (
                  <CircularProgress size={16} />
                ) : (
                  <SaveAltSharp />
                )
              }
              disabled={isDownloading || isLoading}
              onClick={handleDownloadDetailed}
            >
              Download Detalhado
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            my: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h4"
            sx={theme => ({
              fontSize: theme.typography.pxToRem(26),
              fontWeight: 500,
            })}
          >
            {getAdvertiserQuery.data?.name ?? 'Carregando...'}
          </Typography>

          {hasSelectedCampaigns && (
            <Typography
              variant="body2"
              sx={theme => ({
                color: theme.palette.primary.main,
                fontWeight: 500,
              })}
            >
              {selectedCampaigns.length} campanha(s) selecionada(s)
            </Typography>
          )}
        </Box>

        <StyledTableContainer>
          <ReportCampaignsList
            advertiserId={advertiserId}
            getReportsCampaignsQuery={getReportsCampaignsQuery}
            selectedCampaigns={selectedCampaigns}
            onSelectCampaign={handleSelectCampaign}
            onSelectAll={handleSelectAll}
          />
        </StyledTableContainer>

        <Pagination
          disabled={totalPages === 1 || isLoading || total === 0}
          page={1}
          count={1}
          nextPage={nextPageHandler}
          prevPage={prevPageHandler}
          onChange={(_, newPage) =>
            setReportsFilter((state: ReportsCampaignsDto) => ({
              ...state,
              page: newPage,
            }))
          }
        />
      </StyledContainer>
    </>
  );
}
